import {Component, inject, OnInit, input, output} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {TranslocoPipe} from '@jsverse/transloco';
import {Observable} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {RichTextEditorComponent} from '../../../../../../../shared/components/rich-text-editor/rich-text-editor.component';
import {ProjectCharterScopeResponse, ProjectCharterScopeUpsertRequest} from '@frontend/shared-api';
import {Step12ProjectCharterService} from '../step-1-2-project-charter.service';
import {
  ProjectStepRichTextService,
  RichTextSection,
  RichTextSectionConfig,
} from '../../../../../../../shared/services/project-step-rich-text.service';
import {TabStatus} from '../../../../../../../shared/tabs/tab-status.model';
import {ProjectBaseStepComponent} from '../../../../project-base-step/project-base-step.component';
import {SECTION_KEYS} from '../../../../../../../shared/models/charter.types';

type ScopeField = Exclude<keyof ProjectCharterScopeUpsertRequest, 'projectId'>;

@Component({
  selector: 'app-step-1-2-project-chart-scope',
  providers: [ProjectStepRichTextService],
  imports: [ReactiveFormsModule, TranslocoPipe, RichTextEditorComponent],
  templateUrl: './step-1-2-project-chart-scope.component.html',
})
export class Step12ProjectChartScopeComponent extends ProjectBaseStepComponent implements OnInit {
  private readonly chartService = inject(Step12ProjectCharterService);
  readonly richTextService = inject(
    ProjectStepRichTextService<ProjectCharterScopeUpsertRequest, ProjectCharterScopeResponse>,
  );

  readonly preloadedData = input<ProjectCharterScopeResponse>();
  readonly statusChange = output<TabStatus>();

  onStatusChange = () => {
    this.emitStatusChange();
  };

  get scopeSections() {
    return this.richTextService.getSections();
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.initializeRichTextService();
  }

  private initializeRichTextService(): void {
    const config: RichTextSectionConfig<
      ProjectCharterScopeUpsertRequest,
      ProjectCharterScopeResponse
    > = {
      sections: SCOPE_SECTIONS,
      fieldMap: SCOPE_FIELD_MAP,
      saveFunction: (projectId, payload) => this.saveWithCompleteDto(projectId, payload),
      loadFunction: (projectId) => this.chartService.getCharterScope(projectId),
    };

    this.richTextService.initialize(this.projectId, config, this.preloadedData());
  }

  private saveWithCompleteDto(
    projectId: number,
    richTextPayload: ProjectCharterScopeUpsertRequest,
  ): Observable<ProjectCharterScopeResponse> {
    return this.chartService.getCharterScope(projectId).pipe(
      switchMap((existingData) => {
        const completePayload: ProjectCharterScopeUpsertRequest = {
          ...existingData,
          ...richTextPayload,
        };
        return this.chartService.saveCharterScope(projectId, completePayload);
      }),
    );
  }

  protected emitStatusChange(): void {
    const totalSections = this.richTextService.getTotalSectionsCount();
    const completedSections = this.richTextService.getCompletedSectionsCount();
    this.statusChange.emit({totalSections, completedSections});
  }
}

export const SCOPE_SECTIONS: RichTextSection[] = [
  {
    key: SECTION_KEYS.IN_SCOPE,
    content: '',
    tooltipKey: 'project.steps.projectChart.inScope',
    hasChanges: false,
  },
  {
    key: SECTION_KEYS.OUT_OF_SCOPE,
    content: '',
    tooltipKey: 'project.steps.projectChart.outOfScope',
    hasChanges: false,
  },
];

export const SCOPE_FIELD_MAP: Record<string, ScopeField> = {
  [SECTION_KEYS.IN_SCOPE]: 'inScope',
  [SECTION_KEYS.OUT_OF_SCOPE]: 'outOfScope',
};
